// src/components/DriftFullscreen.js
// The "Drift" concept: a fullscreen, vertical snap-scroll feed (Reels/TikTok
// style). Videos autoplay when their slide is in view; a right-side action rail
// handles like / comment / remix / share. Mirrors the mobile FullscreenDriftFeed.

import { useEffect, useRef, useState } from "react";
import Avatar from "./Avatar";
import { drift } from "../api";

const fmt = (n = 0) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);

export default function DriftFullscreen({ posts, user, onRequireAuth, onRemix, onOpenCreator, onOpenComments }) {
  const [active, setActive] = useState(0);
  const [liked, setLiked] = useState({}); // postId -> bool (optimistic)
  const [likeDelta, setLikeDelta] = useState({}); // postId -> +/-
  const [copied, setCopied] = useState(null);
  const itemRefs = useRef([]);
  const videoRefs = useRef([]);
  const viewedRef = useRef({});

  // Track which slide is centered → set active (autoplay + view tracking).
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.6) {
            const idx = Number(e.target.dataset.idx);
            setActive(idx);
          }
        });
      },
      { threshold: [0.6] }
    );
    itemRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [posts]);

  // Play the active video, pause the rest; fire a view once per post.
  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === active) v.play?.().catch(() => {});
      else v.pause?.();
    });
    const p = posts[active];
    if (p && !viewedRef.current[p._id]) {
      viewedRef.current[p._id] = true;
      drift.view(p._id, 3).catch(() => {});
    }
  }, [active, posts]);

  const like = async (p) => {
    if (!user) return onRequireAuth();
    const isLiked = !liked[p._id];
    setLiked((s) => ({ ...s, [p._id]: isLiked }));
    setLikeDelta((s) => ({ ...s, [p._id]: (s[p._id] || 0) + (isLiked ? 1 : -1) }));
    try {
      await drift.like(p._id);
    } catch (_) {
      setLiked((s) => ({ ...s, [p._id]: !isLiked }));
      setLikeDelta((s) => ({ ...s, [p._id]: (s[p._id] || 0) + (isLiked ? -1 : 1) }));
    }
  };

  const share = async (p) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/drift/${p._id}`);
      setCopied(p._id);
      setTimeout(() => setCopied(null), 1500);
    } catch (_) {}
    drift.trackShare(p._id).catch(() => {});
  };

  return (
    <div className="drift-fs">
      {posts.map((p, i) => {
        const isVideo = p.type === "video";
        const likes = (p.likes_count ?? p.likes ?? 0) + (likeDelta[p._id] || 0);
        return (
          <div className="drift-fs__item" key={p._id} data-idx={i} ref={(el) => (itemRefs.current[i] = el)}>
            {isVideo ? (
              <video
                ref={(el) => (videoRefs.current[i] = el)}
                className="drift-fs__media"
                src={p.media_url}
                loop
                muted
                playsInline
                poster={p.thumbnail_url}
              />
            ) : (
              <img className="drift-fs__media" src={p.media_url || p.thumbnail_url} alt={p.prompt || ""} />
            )}

            <div className="drift-fs__side">
              <button className={`dfa ${liked[p._id] ? "dfa--on" : ""}`} onClick={() => like(p)}>
                <span>♥</span><b>{fmt(likes)}</b>
              </button>
              <button className="dfa" onClick={() => onOpenComments(p)}>
                <span>💬</span><b>{fmt(p.comments_count ?? p.comment_count ?? 0)}</b>
              </button>
              <button className="dfa" onClick={() => (user ? onRemix(p) : onRequireAuth())}>
                <span>⟳</span><b>{fmt(p.remix_count ?? 0)}</b>
              </button>
              <button className="dfa" onClick={() => share(p)}>
                <span>↗</span><b>{copied === p._id ? "✓" : ""}</b>
              </button>
            </div>

            <div className="drift-fs__meta">
              <button className="drift-fs__creator" onClick={() => onOpenCreator?.(p.creator_id)}>
                <Avatar name={p.creator_username || "user"} url={p.creator_avatar_url} size={30} />
                <span>@{p.creator_username || "user"}</span>
              </button>
              {p.prompt && <p className="drift-fs__prompt">{p.prompt}</p>}
              <button className="drift-fs__remix" onClick={() => (user ? onRemix(p) : onRequireAuth())}>⟳ Remix this</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
